import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Grid, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { useHasClerkRole } from '../../common/hooks/useHasClerkRole';
import { useHasClerkPermissions } from '../../common/hooks/useHasClerkPermissions';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { fetchEmployees, selectAllEmployees } from '../../slices/employees/employeesSlice';
import { EmployeesCreateModal } from '../company-create/components/modal/EmployeesCreateModal';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { Employee } from '../../models/employee.model';
import styles from './EmployeeProfiles.module.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { NWClient } from '../../client/NWClient';
import MessageModal from '../../common/message-modal/MessageModal';
import { useOrganization } from '@clerk/clerk-react';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import EmploymentInfoText from '../library/components/employment-info-text/EmploymentInfoText';
import Preview from './components/preview-block/Preview';
import { Helpers } from '../../common/helpers/helpers';

const EmployeeProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const checkPermission = useCheckPermission();
  const [isOpenEmployeesCreation, setIsOpenEmployeesCreation] = useState(false);
  const [planMessageVisible, setPlanMessageVisible] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);
  const [employmentDataMessage, setEmploymentDataMessage] = useState(false);
  const [invitation, setInvitation] = useState<any>(null);
  const handleClose = () => {
    setIsOpenEmployeesCreation(false);
  };
  const company = useAppSelector(selectAllCompanies)[0];
  const employeesCreatedByAdmin = useAppSelector(selectAllEmployees);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  // This logic is tied to company member employees which do not have a full_name attribute (created by sign up)

  const { invitations } = useOrganization({
    invitations: true,
    //memberships: true,
  });

  // Examples of how to use hooks
  const isAdmin = useHasClerkRole('org:admin');
  const hasEditPermissions = useHasClerkPermissions(['org:sys_memberships:manage']);

  const [employeeId, setEmployeeId] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const employeeUserId = employeesCreatedByAdmin?.find((el) => el.id === employeeId)?.user;

  const employeeJoinedCompany = Boolean(
    company?.members.find((el) => el.id === employeeUserId)?.user_uuid
  );
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setEmployeeId(id);
  };
  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const handleProfileDelete = () => {
    if (employeeId) {
      NWClient.delete('employee', employeeId).then(() => {
        if (invitation) {
          invitation.revoke();
        }
        setDeleteMessageModalOpen(false);
        setEmployeeId(null);
        setInvitation(null);
        dispatch(fetchEmployees({ company_id: company?.id }));
        setAnchorEl(null);
      });
    }
  };

  const handleEditButton = (id: number) => {
    navigate(ROUTES.EMPLOYEE_PROFILES + '/' + id + '?initialTab=1');
  };

  const handleDocumentsButton = (id: number) => {
    navigate(ROUTES.EMPLOYEE_PROFILES + '/' + id + '?initialTab=0');
  };

  const handleCreateEmployee = () => {
    const planSettings = Helpers.getPlanSettings(company);
    if (!planSettings) return;

    const limit = planSettings.employee_profile;
    const count = employeesCreatedByAdmin?.length ?? 0;

    if (limit === 0) {
      setPlanMessageVisible(true);
    } else if (limit !== 'unlimited' && count >= limit) {
      setPlanMessageVisible(true);
    } else {
      navigate(ROUTES.EMPLOYEE_PROFILE_CREATE);
    }
  };

  const fetchEmployeesArray = useCallback(() => {
    if (employeesLoading === 'idle') {
      dispatch(fetchEmployees({ company_id: company?.id }));
    }
  }, [company, dispatch, employeesLoading]);

  const fetchEmployeeInvitation = useCallback(() => {
    if (employeeId) {
      const invitationList = invitations?.data;
      const employeeData = employeesCreatedByAdmin?.find((el) => el.id === employeeId);
      const invitationItem = invitationList?.find(
        (invite) =>
          invite.emailAddress === employeeData?.email_address ||
          invite.emailAddress === employeeData?.business_email_address
      );
      if (invitationItem) {
        setInvitation(invitationItem);
      }
    }
  }, [employeeId, invitations?.data, employeesCreatedByAdmin, setInvitation]);

  useEffect(() => {
    fetchEmployeesArray();
  }, [fetchEmployeesArray]);

  useEffect(() => {
    fetchEmployeeInvitation();
  }, [fetchEmployeeInvitation]);

  return (
    <Grid container sx={{ mt: '-61px' }}>
      <Box display='flex' flexDirection='column' gap='8px' alignItems='flex-start'>
        <Typography variant='h1' component='h1' className={styles.page_title}>
          <span style={{ marginRight: '8px' }}>{t('employeeProfiles.header')}</span>
          <PreviewTooltip title={<Preview />} arrow placement='top'>
            <InfoIcon color='primary' fontSize='small' />
          </PreviewTooltip>
        </Typography>
        <Tooltip
          title={t('employmentTemplatesInfo.employmentDocumentGuidance')}
          placement='top'
          arrow
        >
          <Button
            variant='contained'
            className='app-bar-link white-variant min-width-inherit'
            onClick={() => {
              setEmploymentDataMessage(true);
            }}
          >
            <PriorityHighIcon fontSize='medium' />
          </Button>
        </Tooltip>
      </Box>
      <Grid size={{ xs: 12 }} className={styles.employee_cards} sx={{ marginTop: '8px' }}>
        {[...employeesCreatedByAdmin]
          .sort((a, b) => a.full_name.localeCompare(b.full_name))
          .map((employee: Employee) => (
            <Card
              tabIndex={-1}
              key={employee.id}
              onClick={() => navigate(ROUTES.EMPLOYEE_PROFILES + '/' + employee.id)}
              className={styles.employee_card}
            >
              {employee.full_name && (
                <Typography
                  fontWeight='regular'
                  className={`${styles.employee_name} ${
                    employee.full_name && employee.full_name.length > 30 ? styles.long_text : ''
                  }`}
                  title={employee.full_name}
                >
                  {employee.full_name}
                </Typography>
              )}
              <Box className={styles.employee_profile_icons}>
                {checkPermission(USER_PERMISSIONS.ORG_DOCUMENT_UPLOAD_CREATE) ||
                checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE_DOCUMENT) ||
                checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_DELETE_DOCUMENT) ? (
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDocumentsButton(employee.id);
                    }}
                    size='small'
                  >
                    <UploadFileOutlinedIcon fontSize='small' />
                  </IconButton>
                ) : (
                  <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDocumentsButton(employee.id);
                      }}
                      disabled={true}
                      size='small'
                    >
                      <UploadFileOutlinedIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
                {checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE) ? (
                  <IconButton
                    aria-label={t('labels.menuButton')}
                    disabled={!hasEditPermissions}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleEditButton(employee.id);
                    }}
                    size='small'
                    sx={{ p: 0.5 }}
                  >
                    <EditIcon fontSize='small' />
                  </IconButton>
                ) : (
                  <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
                    <IconButton
                      disabled={true}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditButton(employee.id);
                      }}
                      size='small'
                      sx={{ p: 0.5 }}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  aria-label={t('labels.menuButton')}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClick(event, employee.id);
                  }}
                  disabled={!checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_DELETE)}
                  size='small'
                  sx={{ p: 0.5 }}
                >
                  <MoreVertIcon fontSize='small' />
                </IconButton>
              </Box>
            </Card>
          ))}
        {checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_CREATE) ? (
          <Button
            onClick={handleCreateEmployee}
            variant='contained'
            color='primary'
            className={styles.add_button}
            disabled={!isAdmin}
          >
            <Box
              sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '1rem' }}
            >
              <Typography fontWeight='light' fontSize={14}>
                {t('buttons.addProfile')}
              </Typography>
              <AddIcon />
            </Box>
          </Button>
        ) : (
          <Tooltip title={t('messages.contactAdministrator')} arrow placement='top'>
            <Button
              onClick={null}
              variant='contained'
              color='primary'
              className={styles.add_button}
              disabled={true}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: '1rem' }}
              >
                <Typography fontWeight='light' fontSize={14}>
                  {t('buttons.addProfile')}
                </Typography>
                <AddIcon />
              </Box>
            </Button>
          </Tooltip>
        )}
      </Grid>
      <EmployeesCreateModal open={isOpenEmployeesCreation} onClose={handleClose} />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMoreVertClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={employeeJoinedCompany ? null : () => setDeleteMessageModalOpen(true)}>
          {employeeJoinedCompany || checkPermission(USER_PERMISSIONS.ORG_MEMBERSHIP_DELETE)
            ? t('buttons.employeeDeletionNotAvailable')
            : t('buttons.delete')}
        </MenuItem>
      </Menu>

      {planMessageVisible && (
        <MessageModal
          open={planMessageVisible}
          modalMessage={t('messages.limitEmployees')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => navigate(ROUTES.PRICING)}
          goBackButtonText={t('buttons.later')}
          onClose={() => {
            setPlanMessageVisible(false);
          }}
        />
      )}
      {employmentDataMessage && (
        <MessageModal
          open={employmentDataMessage}
          modalTitle={t('employmentTemplatesInfo.employmentDocumentGuidance')}
          modalContent={<EmploymentInfoText />}
          headerVariant={'h5'}
          goBackButtonText={t('buttons.close')}
          noActionButton={true}
          onClose={() => {
            setEmploymentDataMessage(false);
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('messages.deleteEmployee')}
          modalMessage={t('messages.deleteEmployeeDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => setDeleteMessageModalOpen(false)}
          goBackButtonText={t('buttons.delete')}
          onClose={() => {
            handleProfileDelete();
          }}
        />
      )}
    </Grid>
  );
};

export default EmployeeProfiles;
